import React from 'react'
import qs from 'qs'
import { Prompt, useHistory, useLocation } from 'react-router'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'

import TimelineTargetProvider from '@SonarCX/providers/TimelineTargetProvider'

import AgentPage from '../../components/templates/AgentPage'
import NewConversationCountProvider from '../../providers/NewConversationCountProvider'
import Spacer from '../../components/atoms/Spacer'
import Conversations from '../../components/organisms/Conversations'
import ChatWindow from '../../components/organisms/ChatWindow'
import ConversationScrollProvider from '../../providers/ConversationScrollProvider'
import AddRemoveUserModal from '../../components/molecules/ChatModals/AddRemoveParticipantModal'
import AreYouSureModal from '../../components/molecules/ChatModals/AreYouSureModal'

import { Routes } from '../../Routes'
import { removeFromStack, removeParticipant } from '../../lib/api'
import { pullDraft, saveDraft } from '../../lib/idb'
import {
  selectChatControls,
  reset,
  loadDraft,
  resetDraft
} from '../../slices/chatControlsSlice'
import { useSocket } from '../../providers/SocketProvider'
import { useQueueState } from '../../providers/QueueStateProvider'
import { useUserAPI } from '../../providers/UserAPIProvider'
import { removeDM, selectDMs, setSelected } from '../../slices/dms'
import { useAddRemoveParticipant } from '../../providers/AddRemoveParticipantProvider'
import { useAgent } from '../../providers/AgentProvider'
import { useMenuVisibility } from '../../providers/MenuVisibilityProvider'
import { useBreakpoint } from '../../providers/BreakpointProvider'
import useVisualViewport from '../../hooks/useVisualViewport'
import useAudioRecorder from '../../hooks/useAudioRecorder'

const LAST_CONVO = 'last_convo'

const PeerChat = () => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useAppDispatch()

  const {
    allAgents,
    dashboard: { agents: dashboardAgents },
    api: {
      fetchTimeline,
      markSeen,
      messageHandler: messageHandlerAPI,
      typeHandler: typeHandlerAPI,
      throttledTypingEvent,
      rejectDM
    },
    peerChat: { setNewPush }
  } = useSocket()
  const {
    id: selectedConversation,
    conversation,
    conversations: dms,
    handleSelect: handleConvoSelect,
    handleClose
  } = useQueueState()
  const { allUsers, loaded: usersLoaded } = useUserAPI()
  const { queueLoaded } = useAppSelector(selectDMs)
  const { message, file } = useAppSelector(selectChatControls)
  const { currentUser } = useAgent()
  const {
    setCurrentUserIDs,
    currentUserIDs,
    editSessionUsers,
    setEditSessionUsers,
    leave,
    setLeave
  } = useAddRemoveParticipant()
  const { show: showMenu, hide: hideMenu } = useMenuVisibility()
  const { down767: onMobile } = useBreakpoint()
  const { isKeyboardOn } = useVisualViewport()
  const isTextAreaActiveOnMobile = onMobile && isKeyboardOn
  const {
    startRecording,
    stopRecording,
    recordingBlob,
    isRecording,
    recordingTime,
    clearRecord
  } = useAudioRecorder()

  const [fetching, setFetching] = React.useState([])
  const [showQueue, setShowQueue] = React.useState(false)
  const [previousConvoHasBeenSelected, setPreviousConvoHasBeenSelected] =
    React.useState(false)

  const agents = React.useMemo(
    () => dashboardAgents.map(({ id, name }) => ({ id, display: name })),
    [dashboardAgents]
  )

  // Fetches conversation on first selecting
  React.useEffect(() => {
    if (!conversation || !usersLoaded) {
      return
    }

    if (fetching.includes(selectedConversation)) {
      return
    }

    const timer = setTimeout(() => {
      // condition for searching user or session for dms
      const userExists = allUsers[selectedConversation]

      if (!conversation?.fetched) {
        const selectedID = selectedConversation

        setFetching(old => [...new Set([...old, selectedID])])

        if (userExists) {
          // @ts-ignore
          fetchTimeline(selectedID, true).then(() =>
            setFetching(old => old?.filter(id => id !== selectedID))
          )
        } else {
          fetchTimeline(selectedID, true, true).then(() =>
            setFetching(old => old?.filter(id => id !== selectedID))
          )
        }
      }
    }, 1250)

    return () => {
      clearTimeout(timer)
    }
  }, [
    selectedConversation,
    conversation,
    fetching,
    fetchTimeline,
    allUsers,
    usersLoaded
  ])

  // Selects conversation from params
  React.useEffect(() => {
    const params = qs.parse(location.search, { ignoreQueryPrefix: true })
    const { session, isUser } = params

    // @ts-ignore
    if (!(session in dms)) {
      return
    }

    // @ts-ignore
    if (isUser && !(session in allUsers)) {
      return
    }

    handleConvoSelect(session, () => {
      markSeen(session, true)
      const newParams = { ...params }
      delete newParams.session
      delete newParams.isUser
      history.replace(
        `${Routes.PEER_CHAT}/${qs.stringify(newParams, {
          encode: false,
          addQueryPrefix: true
        })}`
      )
    })
  }, [dms, allUsers, location, handleConvoSelect, history, markSeen])

  // Clear new status from current dm
  React.useEffect(() => {
    if (!conversation) {
      return
    }

    if (conversation.isNew) {
      markSeen(selectedConversation, true)
    }
  }, [conversation, selectedConversation, markSeen])

  // Saves current message draft state and loads next sessions
  const updateDraftStorage = React.useCallback(
    async (next?: string) => {
      const userID = currentUser()?.id
      if (!userID) {
        dispatch(reset())
        return
      }

      if (selectedConversation !== '')
        await saveDraft(userID, selectedConversation, { message, file })

      if (!next) {
        dispatch(reset())
        return
      }

      const data = await pullDraft(userID, next)
      if (data) {
        dispatch(loadDraft({ message: data.message, file: data.file }))
      } else {
        dispatch(resetDraft())
      }
    },
    [currentUser, selectedConversation, message, file, dispatch]
  )

  //  Callback for when a conversation is selected
  const handleSelect = React.useCallback(
    async session => {
      await updateDraftStorage(session)
      if (isRecording) {
        localStorage.setItem('cancel-save', 'true')
        stopRecording()
      }

      handleConvoSelect(session, () => {
        markSeen(session, true)
        sessionStorage.removeItem('users')
        const isGroup = !(session in allUsers)
        sessionStorage.setItem(
          LAST_CONVO,
          JSON.stringify({ session, isUser: !isGroup })
        )
      })

      setNewPush(old => old.filter(o => o !== session))
    },
    [
      handleConvoSelect,
      markSeen,
      setNewPush,
      allUsers,
      updateDraftStorage,
      isRecording,
      stopRecording
    ]
  )

  // setting current users in convo to provider
  React.useEffect(() => {
    if (!conversation) {
      return
    }

    setCurrentUserIDs(
      conversation.users?.filter(id => id !== currentUser()?.id)
    )
  }, [conversation, currentUser, setCurrentUserIDs])

  // set last open convo
  React.useEffect(() => {
    if (onMobile) return
    const lastConvo = sessionStorage.getItem(LAST_CONVO)
    if (!lastConvo) return

    if (queueLoaded && !selectedConversation && !previousConvoHasBeenSelected) {
      const { session, isUser } = JSON.parse(lastConvo)
      if (!(session in dms)) {
        return
      }
      if (isUser && !(session in allUsers)) {
        return
      }
      handleSelect(session)
      setPreviousConvoHasBeenSelected(true)
    }
  }, [
    queueLoaded,
    selectedConversation,
    dms,
    allUsers,
    onMobile,
    previousConvoHasBeenSelected,
    handleSelect
  ])

  //  Callback for sending a message through the api
  const messageHandler = React.useCallback(
    (message, mode, file) => {
      // check if selected conversation is in users - target session or user
      const isGroup = !(selectedConversation in allUsers)

      messageHandlerAPI(
        message,
        mode,
        file,
        selectedConversation,
        true,
        isGroup
      )
    },
    [messageHandlerAPI, selectedConversation, allUsers]
  )

  // Handles changing the type of the current conversation
  const typeHandler = React.useCallback(
    type => {
      typeHandlerAPI(selectedConversation, type, true)
    },
    [typeHandlerAPI, selectedConversation]
  )

  const handleTyping = React.useCallback(() => {
    throttledTypingEvent(selectedConversation, true)
  }, [throttledTypingEvent, selectedConversation])

  const handleCloseDM = React.useCallback(
    id => {
      handleClose(id, () => {
        rejectDM(id)
      })
    },
    [handleClose, rejectDM]
  )

  const handleLeaveConvo = React.useCallback(async () => {
    // handler function to remove convo from queue

    // send part message via socket
    try {
      await removeParticipant(selectedConversation, currentUser()?.id)
      dispatch(removeDM(selectedConversation))
      await removeFromStack(selectedConversation, 'default')
      setLeave(false)
      handleConvoSelect(null)
    } catch (err) {
      console.error('Error leaving convo', err)
    }
  }, [selectedConversation, dispatch, setLeave, currentUser, handleConvoSelect])

  // Setting state for mobile view
  React.useEffect(() => {
    if (onMobile) {
      setShowQueue(!Boolean(selectedConversation))
    } else {
      setShowQueue(true)
    }
  }, [onMobile, selectedConversation])

  // Hide top menu when on a chat
  React.useEffect(() => {
    showQueue ? showMenu() : hideMenu()
  }, [showQueue, selectedConversation, showMenu, hideMenu])

  return (
    <AgentPage hideMobileFooter={isTextAreaActiveOnMobile}>
      {showQueue && (
        <NewConversationCountProvider>
          <ConversationScrollProvider>
            <Conversations
              conversations={dms}
              current={selectedConversation}
              selectHandler={handleSelect}
              doneLoading={queueLoaded}
              allAgents={allAgents}
              isDirect
              // @ts-ignore
              handleCloseConversation={handleCloseDM}
            />
          </ConversationScrollProvider>
        </NewConversationCountProvider>
      )}

      {!conversation ? (
        <Spacer />
      ) : !onMobile || !showQueue ? (
        <>
          <TimelineTargetProvider>
            {/* @ts-ignore */}
            <ChatWindow
              convo={conversation}
              onMessageSubmit={messageHandler}
              onTypeSet={typeHandler}
              onTyping={handleTyping}
              agents={agents}
              setShowQueue={setShowQueue}
              onMobile={onMobile}
              isDirect
              startRecording={startRecording}
              stopRecording={stopRecording}
              recordingBlob={recordingBlob}
              isRecording={isRecording}
              recordingTime={recordingTime}
              clearRecord={clearRecord}
            />
          </TimelineTargetProvider>

          {editSessionUsers && (
            <AddRemoveUserModal
              isDirect
              convo={conversation}
              open={editSessionUsers}
              onClose={() => setEditSessionUsers(false)}
              users={currentUserIDs}
            />
          )}

          {leave && (
            <AreYouSureModal
              open={leave}
              onClose={() => setLeave(false)}
              onAccept={handleLeaveConvo}
              reason={'You will no longer be able to access this conversation.'}
              subtext={''}
            />
          )}
        </>
      ) : null}

      {/* Deselects a convo when moving away */}
      <Prompt
        message={location => {
          if (!location.pathname.startsWith(Routes.PEER_CHAT)) {
            if (isRecording) {
              localStorage.setItem('cancel-save', 'true')
              stopRecording()
            }
            updateDraftStorage()
            dispatch(setSelected(null))
            handleConvoSelect(null)
            showMenu()
          }
          return true
        }}
        when
      />
    </AgentPage>
  )
}

export default PeerChat
